import styled from 'styled-components'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useGetAssetById } from '../../../api/contentful/contentfulQueries'
import { CombinedHealthCard } from '../../../api/ester-b2b-api/private/healthPlan/types'
import { parseContenfulImageUrl } from '../../../contentful/utils'
import { theme } from '../../../theme/theme'
import { getHealthCardModuleImageId, DEFAULT_MODULE_IMAGE } from '../../../utils/imageMapper'
import { Close } from '../../icons'
import { Title2, Title6 } from '../../styles/Typography'
import { HealthPlanCardCollapsible } from './HealthPlanCardCollapsible'
import { useSelectScreeningStatus } from '../../../state/selectors'
import { ScreeningStatus } from '@estercare/ester-shared'
import { NewRecommendationsBanner } from '../NewRecommendationsBanner'

const slideVariants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -1000 : 1000,
    opacity: 0,
  }),
}

export const HealthPlanCard = ({
  card,
  onCardClick,
  isExpanded = false,
  onClose,
}: {
  card: CombinedHealthCard
  onCardClick: (card: CombinedHealthCard) => void
  isExpanded?: boolean
  onClose?: () => void
}) => {
  const screeningStatus = useSelectScreeningStatus()
  const isFollowUpHandled = screeningStatus === ScreeningStatus.FOLLOW_UP_HANDLED
  const [[page, direction], setPage] = useState([0, 0])
  const assetId = getHealthCardModuleImageId(card.module)
  const { data: imageAsset } = useGetAssetById(assetId)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isExpanded) {
      setPage([0, 0])
    }
  }, [isExpanded])

  const handleClick = () => {
    if (isExpanded) return
    onCardClick(card)
  }

  const scrollToTop = (element: HTMLElement) => {
    if (scrollContainerRef.current) {
      const containerTop = scrollContainerRef.current.getBoundingClientRect().top
      const elementTop = element.getBoundingClientRect().top
      const offsetTop = elementTop - containerTop + scrollContainerRef.current.scrollTop

      scrollContainerRef.current.scrollTo({ top: offsetTop, behavior: 'smooth' })
    }
  }

  return (
    <CardContainer
      layoutId={`card-${card.module}`}
      $isExpanded={isExpanded}
      onClick={handleClick}
      initial={false}
      transition={{
        layout: { duration: 0.5, ease: [0.65, 0, 0.35, 1] },
      }}
    >
      <CloseButton $isExpanded={isExpanded} onClick={onClose}>
        <Close size={24} color={theme.color.white} />
      </CloseButton>

      <ImageContainer $isExpanded={isExpanded}>
        <img
          src={
            imageAsset
              ? isExpanded
                ? parseContenfulImageUrl(imageAsset, { width: 2400, height: 1000 })
                : parseContenfulImageUrl(imageAsset, { width: 800, height: 400 })
              : DEFAULT_MODULE_IMAGE
          }
          alt={card.title}
        />
        {isExpanded && (
          <ImageTitle
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {card.title}
          </ImageTitle>
        )}
      </ImageContainer>

      <ContentContainer $isExpanded={isExpanded}>
        {!isExpanded && !!card.careLevel && isFollowUpHandled && <NewRecommendationsBanner compact />}
        {!isExpanded && <CompactTitle>{card.title}</CompactTitle>}

        <ContentWrapper $isExpanded={isExpanded} ref={scrollContainerRef}>
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <SlideContent
              key={page}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'tween', ease: 'easeInOut', duration: 0.3 },
              }}
            >
              {card.content.map((content, index) => (
                <HealthPlanCardCollapsible
                  key={index}
                  content={content}
                  isExpanded={isExpanded}
                  onOpen={scrollToTop}
                  carePlan={!!card.careLevel}
                />
              ))}
            </SlideContent>
          </AnimatePresence>
        </ContentWrapper>
      </ContentContainer>

      <Gradient $isExpanded={isExpanded} />
    </CardContainer>
  )
}

const CardContainer = styled(motion.div)<{ $isExpanded: boolean }>`
  position: relative;
  background-color: ${theme.color.white};
  cursor: ${({ $isExpanded }) => ($isExpanded ? 'default' : 'pointer')};
  border-radius: ${theme.spacing.medium}px;
  overflow: hidden;
  box-shadow: ${({ $isExpanded }) => ($isExpanded ? 'none' : '0px 4px 16px rgba(0, 0, 0, 0.1)')};
  ${({ $isExpanded }) =>
    $isExpanded
      ? `
    flex: 0 0 100%;
    width: 100%;
  `
      : `
    flex: 0 0 calc(50% - ${theme.spacing.large}px / 2);
    width: calc(50% - ${theme.spacing.large}px / 2);
  `}
  height: auto;
  min-height: ${({ $isExpanded }) => ($isExpanded ? '700px' : '0')};
  display: flex;
  flex-direction: column;
  will-change: transform;
  @media (max-width: ${theme.breakpoint.medium}px) {
    flex: ${({ $isExpanded }) => ($isExpanded ? '1' : '0 0 auto')};
    width: auto;
    max-width: 400px;
    margin: 0 auto;
    height: auto;
    min-height: ${({ $isExpanded }) => ($isExpanded ? '600px' : '0')};
  }
`
const IconCircle = styled.button`
  background-color: ${theme.color.white};
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${theme.color.black};

  &:hover {
    background-color: ${theme.color.grey2};
  }
`

const CloseButton = styled(IconCircle)<{ $isExpanded: boolean }>`
  position: absolute;
  top: ${theme.spacing.medium}px;
  left: ${theme.spacing.medium}px;
  background-color: rgba(255, 255, 255, 0.5);
  display: ${({ $isExpanded }) => ($isExpanded ? 'flex' : 'none')};
  z-index: 10;
`

const ImageContainer = styled(motion.div)<{ $isExpanded: boolean }>`
  position: relative;
  height: ${({ $isExpanded }) => ($isExpanded ? '150px' : '232px')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-bottom-left-radius: ${({ $isExpanded }) => ($isExpanded ? '0' : `${theme.spacing.medium}px`)};
    border-bottom-right-radius: ${({ $isExpanded }) => ($isExpanded ? '0' : `${theme.spacing.medium}px`)};
  }

  ${({ $isExpanded }) =>
    $isExpanded &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100%;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
      pointer-events: none;
    }
  `}

  @media (max-width: ${theme.breakpoint.medium}px) {
    height: ${({ $isExpanded }) => ($isExpanded ? '190px' : '180px')};
  }
`

const ImageTitle = styled(motion(Title2))`
  position: absolute;
  bottom: ${theme.spacing.mediumLarge}px;
  left: ${theme.spacing.mediumLarge}px;
  color: ${theme.color.white};
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 1;
`
const ContentContainer = styled.div<{ $isExpanded: boolean }>`
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px ${theme.spacing.small}px;
  ${({ $isExpanded }) => ($isExpanded ? 'height: 500px;' : 'height: 140px;')}
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.medium}px;
    ${({ $isExpanded }) => ($isExpanded ? 'height: 450px;' : '')}
  }
`

const ContentWrapper = styled.div<{ $isExpanded: boolean }>`
  position: relative;
  ${({ $isExpanded }) =>
    $isExpanded &&
    `
    flex: 1;
    overflow-y: scroll;
  `}
  /* Webkit browsers (Chrome, Safari) */
    &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.color.grey1};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.color.grey3};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.color.grey4};
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${theme.color.grey3} ${theme.color.grey1};
  display: flex;
  flex-direction: column;
`

const SlideContent = styled(motion.div)`
  width: 100%;
  flex: 1;
`

const CompactTitle = styled(Title6)`
  margin-top: ${theme.spacing.small}px;
  margin-bottom: ${theme.spacing.tiny}px;
`

const Gradient = styled.div<{ $isExpanded: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${theme.color.white} 100%);
  pointer-events: none;
  display: ${({ $isExpanded }) => ($isExpanded ? 'none' : 'block')};
`
